import * as React from "react";
import { Star } from "lucide-react";
import { cn } from "@/lib/utils";

/** One answer as the details view shows it: stars with the written review, scores, yes/no, sentiment or text. */
export const renderFieldValue = (fieldResp: any) => {
  const fieldType = fieldResp.formField?.fieldType;
  const rawVal = fieldResp.value;

  if (rawVal === null || rawVal === undefined || rawVal === "") {
    return <span className="text-slate-300 italic font-normal">No Answer</span>;
  }

  if (fieldType === "star") {
    let starVal: { rating?: number; text?: string } | null = null;
    if (typeof rawVal === "object") {
      starVal = rawVal;
    } else if (typeof rawVal === "string") {
      try {
        starVal = JSON.parse(rawVal);
      } catch {
        // If not valid JSON, treat as raw number
      }
    }

    if (starVal) {
      const rating = Number(starVal.rating);
      return (
        <div className="space-y-1.5">
          {!isNaN(rating) && (
            <div className="flex items-center gap-0.5 text-amber-400 select-none">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star
                  key={i}
                  className={cn("size-4", i < rating ? "fill-amber-400 text-amber-400" : "text-slate-200")}
                />
              ))}
            </div>
          )}
          {starVal.text && (
            <p className="text-slate-600 italic text-sm border-l-2 border-slate-200 pl-2.5 leading-normal">
              "{starVal.text}"
            </p>
          )}
        </div>
      );
    }

    const rating = Number(rawVal);
    if (!isNaN(rating)) {
      return (
        <div className="flex items-center gap-0.5 text-amber-400 select-none">
          {Array.from({ length: 5 }).map((_, i) => (
            <Star key={i} className={cn("size-4", i < rating ? "fill-amber-400 text-amber-400" : "text-slate-200")} />
          ))}
        </div>
      );
    }
  }

  if (fieldType === "nps" || fieldType === "ten_point") {
    const score = Number(rawVal);
    if (!isNaN(score)) {
      return (
        <span className="font-semibold text-slate-800 text-sm">
          {score} <span className="text-slate-400 font-normal text-xs">/ 10</span>
        </span>
      );
    }
  }

  if (fieldType === "boolean") {
    const isYes = rawVal === true || String(rawVal).toLowerCase() === "true" || String(rawVal).toLowerCase() === "yes";
    return (
      <span className={cn("font-semibold text-sm", isYes ? "text-emerald-600" : "text-amber-600")}>
        {isYes ? "Yes" : "No"}
      </span>
    );
  }

  if (fieldType === "sentiment") {
    const sentimentMap: Record<string, string> = {
      positive: "😊 Positive",
      neutral: "😐 Neutral",
      negative: "😞 Negative",
    };
    const cleaned = String(rawVal).toLowerCase().trim();
    const mapped = sentimentMap[cleaned];
    if (mapped) {
      return <span className="font-semibold text-slate-800 text-sm">{mapped}</span>;
    }
  }

  return <span className="text-slate-700 font-sans break-words">{String(rawVal)}</span>;
};

/** "2m 14s" between starting and finishing, or null while in progress. */
export const responseDuration = (startedStr: string, completedStr: string | null) => {
  if (!completedStr) return null;
  try {
    const start = new Date(startedStr);
    const end = new Date(completedStr);
    const diffMs = end.getTime() - start.getTime();
    if (diffMs <= 0) return null;

    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);

    if (diffHours > 0) {
      const remainingMins = diffMins % 60;
      return `${diffHours}h ${remainingMins}m`;
    }
    if (diffMins > 0) {
      const remainingSecs = diffSecs % 60;
      return `${diffMins}m ${remainingSecs}s`;
    }
    return `${diffSecs}s`;
  } catch {
    return null;
  }
};
